/**
 * toast.ts - Wrapper centralizado para notificações toast (sonner).
 * Responsabilidade: Encapsular sonner com configurações padrão e facilitar uso global.
 */
import { toast as sonnerToast, ExternalToast } from 'sonner';
import { classifyError } from './networkError';

type ToastOptions = ExternalToast;

const AUTH_TOAST_RE = /sessão expirou|faça login novamente|não autenticado/i;
let lastAuthToastKey = '';
let lastAuthToastAt = 0;

function shouldSkipDuplicateAuthToast(message: unknown): boolean {
    if (typeof message !== 'string' || !AUTH_TOAST_RE.test(message)) return false;
    const now = Date.now();
    if (message === lastAuthToastKey && now - lastAuthToastAt < 10_000) return true;
    lastAuthToastKey = message;
    lastAuthToastAt = now;
    return false;
}

/**
 * Serviço de notificações toast.
 * Uso: import { notify } from '@/utils/toast';
 *      notify.success('Mensagem de sucesso');
 */
export const notify = {
    /**
     * Toast de sucesso (verde)
     */
    success: (message: string | React.ReactNode, options?: ToastOptions) => {
        return sonnerToast.success(message, options);
    },

    /**
     * Toast de erro (vermelho)
     */
    error: (message: string | React.ReactNode | Error, options?: ToastOptions) => {
        const display =
            message instanceof Error
                ? classifyError(message).message
                : typeof message === 'string'
                    ? classifyError(message).message
                    : message;
        if (shouldSkipDuplicateAuthToast(display)) return;
        if (typeof display === 'string') {
            console.error('[Toast Error]', display);
        }
        return sonnerToast.error(display, options);
    },

    /**
     * Toast de informação (azul)
     */
    info: (message: string | React.ReactNode, options?: ToastOptions) => {
        if (shouldSkipDuplicateAuthToast(message)) return;
        return sonnerToast.info(message, options);
    },

    /**
     * Toast de aviso (amarelo)
     */
    warning: (message: string | React.ReactNode, options?: ToastOptions) => {
        console.warn('[Toast Warning]', message);
        return sonnerToast.warning(message, options);
    },

    /**
     * Toast de carregamento (loading spinner)
     * Retorna um ID que pode ser usado para atualizar/fechar o toast
     */
    loading: (message: string | React.ReactNode, options?: ToastOptions) => {
        return sonnerToast.loading(message, options);
    },

    /**
   * Toast com promise - mostra loading, sucesso ou erro automaticamente
   */
    promise: <T>(
        promise: Promise<T>,
        messages: {
            loading: string | React.ReactNode;
            success: string | React.ReactNode | ((data: T) => string | React.ReactNode);
            error: string | React.ReactNode | ((error: unknown) => string | React.ReactNode);
        }
    ) => {
        return sonnerToast.promise(promise, messages);
    },

    /**
     * Fechar um toast específico pelo ID
     */
    dismiss: (toastId?: string | number) => {
        return sonnerToast.dismiss(toastId);
    },

    /**
     * Toast customizado (para casos especiais)
     */
    custom: (message: string | React.ReactNode | ((id: string | number) => React.ReactNode), options?: ToastOptions) => {
        return sonnerToast.custom((id) => (typeof message === 'function' ? (message as Function)(id) : message), options);
    },

    /**
     * Toast de confirmação com botões Confirmar/Cancelar
     * Retorna uma Promise que resolve true (confirmado) ou false (cancelado)
     */
    confirm: (message: string, options?: { confirmText?: string; cancelText?: string; description?: string | React.ReactNode }): Promise<boolean> => {
        return new Promise((resolve) => {
            sonnerToast.warning(message, {
                description: options?.description,
                duration: Infinity,
                // Aumentar largura significativamente para o modal de confirmação
                style: { 
                    width: 'auto', 
                    minWidth: '400px',
                    maxWidth: '90vw'
                },
                className: 'group toast-confirm-upgrade', 
                action: {
                    label: options?.confirmText || 'Confirmar',
                    onClick: () => resolve(true),
                },
                cancel: {
                    label: options?.cancelText || 'Cancelar',
                    onClick: () => resolve(false),
                },
                onDismiss: () => resolve(false),
            });
        });
    }
};

// Export default para compatibilidade
export default notify;
